import { makeStyles } from "tss-react/mui";
import { ICON_VARIANTS } from "@miplatform/constants";
import { Box, DialogContent, Modal, Typography } from "@mui/material";
import { MiActions } from "../MiActions/MiActions";
import { MiLoader } from "../MiLoader/MiLoader";
import { ModalHeader } from "./ModalHeader";

/**
 * A customizable modal component with header, content, loader, and action buttons.
 *
 * @component
 * @param {Object} props - The properties of the MiModal component.
 * @param {Array<Object>} [props.actions=[]] - Array of action button configurations to display in the footer.
 * @param {React.ReactNode} [props.children=null] - The content of the modal.
 * @param {Function} [props.closeModal=()=>{}] - Callback triggered when the modal is closed.
 * @param {React.ReactNode} [props.description=""] - Optional description text displayed under the header.
 * @param {boolean} [props.open=false] - Controls whether the modal is open.
 * @param {string} [props.title=""] - Title text displayed in the header.
 * @param {string} [props.titleIcon=""] - Path or identifier for the header icon.
 * @param {string} [props.leftIconColor] - Optional color for the header icon.
 * @param {("contained"|"outlined"|"default")} [props.titleIconVariant=ICON_VARIANTS.CONTAINED] - Variant style of the header icon.
 * @param {Object} [props.titleContainerStyle] - Custom styles for the title container.
 * @param {Object} [props.contentContainerStyle] - Custom styles for the content container.
 * @param {Object} [props.actionsButtonProps] - Props to pass to the action buttons.
 * @param {number|string} [props.width] - Custom width for the modal.
 * @param {boolean} [props.loading=false] - Whether to show a linear loader at the top of the modal.
 *
 * @returns {JSX.Element} The rendered modal component.
 */
export function MiModal({
  actions = [],
  children = null,
  closeModal = () => {},
  description = "",
  open = false,
  title = "",
  titleIcon = "",
  leftIconColor: titleIconColor,
  titleIconVariant = ICON_VARIANTS.CONTAINED,
  titleContainerStyle,
  contentContainerStyle,
  actionsButtonProps,
  width,
  loading = false,
}) {
  const { classes } = useStyles({ width });

  return (
    <Modal open={open} onClose={closeModal} data-testid="modal">
      <Box className={classes.modalContainer}>
        <ModalHeader
          title={title}
          titleIcon={{
            path: titleIcon,
            color: titleIconColor,
            variant: titleIconVariant,
          }}
          titleContainerStyle={titleContainerStyle}
          closeModal={closeModal}
        />
        <MiLoader type="linear" isLoading={loading} />
        {description && (
          <DialogContent className={classes.descriptionSection}>
            <Typography className={classes.description}>
              {description}
            </Typography>
          </DialogContent>
        )}
        <Box className={classes.contentContainer} style={contentContainerStyle}>
          {children}
        </Box>
        {actions.length > 0 && (
          <Box className={classes.btnContainer}>
            <MiActions buttonProps={actionsButtonProps} actions={actions} />
          </Box>
        )}
      </Box>
    </Modal>
  );
}

const useStyles = makeStyles()((theme, { width } = {}) => ({
  modalContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: width || 400,
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
    boxShadow: theme.spacing(6),
  },
  contentContainer: {
    padding: theme.spacing(2, 3),
    color: theme.palette.grey.main,
  },
  descriptionSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(0, 3),
  },
  description: {
    color: theme.palette.grey.medium,
    fontSize: theme.fontSize.medium,
    whiteSpace: "pre-wrap",
  },
  btnContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(0, 3, 3, 3),
    gap: theme.spacing(3),
  },
}));
